import { Component, EventEmitter, HostListener, Input, Output } from '@angular/core';
import {
	AdminConnexZoneOption,
	AdminXpressConnexService,
	ConnexRouteStatus,
	connexRouteStatusToDisplayName,
	emptyBaseAdminRequest,
	GetAdminConnexRouteForEditResponse,
	parseConnexRouteStatusFromApi,
	SaveAdminConnexRouteRequest
} from 'app/services';
import { MessageService } from 'primeng/api';
import { Utils } from 'app/utils';

export interface ConnexRouteSlotFormRow {
	SlotId: number;
	PickupZoneId: number;
	DropoffZoneId: number;
	DepartureTime: string;
	FareAmount: number | null;
	TollAmount: number | null;
	PremiumFareAmount: number | null;
	VehicleCapacity: number | null;
	BookingWindowMinutes: number | null;
	SlotEnabled: boolean;
	expanded: boolean;
	/** Read-only in view; populated when API returns slot driver assignments. */
	assignedDrivers: { displayName: string }[];
}

@Component({
	selector: 'connex-route-edit-drawer',
	templateUrl: './connex-route-edit-drawer.component.html',
	styleUrls: ['./connex-route-edit-drawer.component.scss']
})
export class ConnexRouteEditDrawerComponent {
	private static readonly pesoFmt = new Intl.NumberFormat('en-PH', {
		maximumFractionDigits: 0,
		minimumFractionDigits: 0
	});

	@Output() saved = new EventEmitter<void>();

	@Input()
	public canCreateRoute = false;

	@Input()
	public canUpdateRoute = false;

	public visible = false;
	public editMode = false;
	/** Режим просмотра (макет View Route). */
	public viewMode = false;
	public loadingEdit = false;
	public saving = false;
	/** Модалка подтверждения Disable Route (Figma ConneX SA). */
	public disableRouteDialogVisible = false;
	/** Модалка подтверждения отказа от несохранённых изменений. */
	public showDiscardDialog = false;

	private formSnapshot = '';

	/** Фиксированная ширина панели create / edit / view. */
	public readonly routeDrawerPanelStyle: Record<string, string> = {
		width: '700px'
	};

	public zoneOptions: AdminConnexZoneOption[] = [];

	public routeName = '';
	/** Распарсенный статус маршрута из API; для UI-логики (кнопки, pill). */
	public routeStatus: ConnexRouteStatus | null = null;
	public routeStatusLabel = '';
	public routeCustomersCanBook = true;
	public pickupZoneIds: number[] = [];
	public dropoffZoneIds: number[] = [];
	public slots: ConnexRouteSlotFormRow[] = [];

	/** Сводка для шапки View: «1 Direction • N Slots • M Vehicles». */
	public viewSubtitle = '';
	public viewFareRangeText = '';
	public viewTotalInventory = 0;
	public viewBookingWindowText = '';
	public viewLastUpdatedText = 'N/A';
	public viewUpdatedByText = 'N/A';

	private editRouteId = 0;

	/**
	 * Id маршрута для update; задаётся при открытии Edit/View и не сбрасывается от ложных onHide сайдбара
	 * (оверлеи PrimeNG appendTo="body" могут дергать onHide и обнуляли editRouteId до сохранения).
	 */
	private routeIdForUpdateSession = 0;

	constructor(
		private adminConnex: AdminXpressConnexService,
		private messageService: MessageService
	) {}

	public async openCreate(): Promise<void> {
		if (!this.canCreateRoute) {
			return;
		}
		this.viewMode = false;
		this.editMode = false;
		this.editRouteId = 0;
		this.routeIdForUpdateSession = 0;
		this.resetFormDefaultsForCreate();
		this.takeFormSnapshot();
		this.visible = true;
		await this.ensureZonesLoaded();
	}

	public async openEdit(routeId: number): Promise<void> {
		if (!this.canUpdateRoute) {
			return;
		}
		this.viewMode = false;
		this.editMode = true;
		this.editRouteId = routeId;
		this.routeIdForUpdateSession = routeId;
		this.visible = true;
		this.loadingEdit = true;
		await this.ensureZonesLoaded();
		const ok = await this.loadRoutePayload(routeId);
		this.loadingEdit = false;
		if (!ok) {
			this.visible = false;
			this.resetDrawerSessionState();
			return;
		}
		this.takeFormSnapshot();
	}

	public async openView(routeId: number): Promise<void> {
		this.viewMode = true;
		this.editMode = false;
		this.editRouteId = routeId;
		this.routeIdForUpdateSession = routeId;
		this.visible = true;
		this.loadingEdit = true;
		await this.ensureZonesLoaded();
		const ok = await this.loadRoutePayload(routeId);
		this.loadingEdit = false;
		if (!ok) {
			this.visible = false;
			this.resetDrawerSessionState();
		}
	}

	/** Переход из просмотра в редактирование без повторной загрузки. */
	public startEditFromView(): void {
		if (!this.canUpdateRoute) {
			return;
		}
		this.viewMode = false;
		this.editMode = true;
		if (this.editRouteId > 0) {
			this.routeIdForUpdateSession = this.editRouteId;
		}
		this.takeFormSnapshot();
	}

	/** В View: маршрут в БД Inactive — показываем Enable Route. */
	public get viewFooterShowsEnableRoute(): boolean {
		return this.routeStatus === ConnexRouteStatus.Inactive;
	}

	/** В View: только Active — показываем Disable Route (черновик не выключается из приложения этой кнопкой). */
	public get viewFooterShowsDisableRoute(): boolean {
		return this.routeStatus === ConnexRouteStatus.Active;
	}

	public openDisableRouteDialog(): void {
		if (this.saving || !this.canUpdateRoute || !this.viewFooterShowsDisableRoute || this.editRouteId <= 0) {
			return;
		}
		this.disableRouteDialogVisible = true;
	}

	public closeDisableRouteDialog(): void {
		this.disableRouteDialogVisible = false;
	}

	public async confirmDisableRouteInDialog(): Promise<void> {
		if (this.saving || !this.canUpdateRoute || this.editRouteId <= 0 || !this.viewFooterShowsDisableRoute) {
			return;
		}
		this.saving = true;
		try {
			const res = await this.adminConnex.setConnexRouteActiveState(
				{
					...emptyBaseAdminRequest(),
					RouteId: this.editRouteId,
					SetInactive: true
				},
				this.messageService
			);
			if (!res || res.Success === false) {
				this.messageService.add({
					severity: 'error',
					summary: 'Disable failed',
					detail: res?.ErrorMessage ?? 'Unable to disable route'
				});
				return;
			}
			this.disableRouteDialogVisible = false;
			this.messageService.add({
				severity: 'success',
				summary: 'Route Disabled Successfully',
				detail: 'The route and its associated departure slots are now hidden from the customer app.'
			});
			this.visible = false;
			this.resetDrawerSessionState();
			this.saved.emit();
		} finally {
			this.saving = false;
		}
	}

	public async onEnableRouteFromView(): Promise<void> {
		if (this.saving || !this.canUpdateRoute || !this.viewFooterShowsEnableRoute || this.editRouteId <= 0) {
			return;
		}
		this.saving = true;
		try {
			const res = await this.adminConnex.setConnexRouteActiveState(
				{
					...emptyBaseAdminRequest(),
					RouteId: this.editRouteId,
					SetInactive: false
				},
				this.messageService
			);
			if (!res || res.Success === false) {
				this.messageService.add({
					severity: 'error',
					summary: 'Enable failed',
					detail: res?.ErrorMessage ?? 'Unable to enable route'
				});
				return;
			}
			this.messageService.add({
				severity: 'success',
				summary: 'Route enabled',
				detail: 'The route is now active for customers.'
			});
			this.visible = false;
			this.resetDrawerSessionState();
			this.saved.emit();
		} finally {
			this.saving = false;
		}
	}

	/** Текст пункта App Visibility в модалке Disable (динамика по слотам). */
	public disableConfirmAppVisibilityDetail(): string {
		const n = this.slots.length;
		const slotWord = n === 1 ? 'slot' : 'slots';
		if (n === 0) {
			return `This route will be hidden from the ConneX Customer App immediately.`;
		}
		const times = [...new Set(this.slots.map((s) => (s.DepartureTime || '').trim()))]
			.filter(Boolean)
			.sort();
		if (times.length === 0) {
			return `This route and its departure ${slotWord} will be hidden from the ConneX Customer App immediately.`;
		}
		const labels = times.map((t) => this.formatSlotTime12h(t));
		const joined = this.joinOxfordComma(labels);
		return `This route and its ${joined} ${slotWord} will be hidden from the ConneX Customer App immediately.`;
	}

	/** Текст пункта Booking Impact в модалке Disable. */
	public disableConfirmBookingImpactDetail(): string {
		const caps = this.slots.map((s) => Number(s.VehicleCapacity ?? 0));
		if (!caps.length) {
			return 'Customers will no longer be able to see or book this route.';
		}
		const min = Math.min(...caps);
		const max = Math.max(...caps);
		const per =
			min === max
				? `${min} car${min === 1 ? '' : 's'} per slot`
				: `between ${min} and ${max} cars per slot`;
		return `Customers will no longer be able to see or book the current inventory of ${per}.`;
	}

	private joinOxfordComma(parts: string[]): string {
		if (parts.length === 0) {
			return '';
		}
		if (parts.length === 1) {
			const only = parts[0];
			return only ?? '';
		}
		if (parts.length === 2) {
			return `${parts[0]} and ${parts[1]}`;
		}
		const head = parts.slice(0, -1).join(', ');
		return `${head}, and ${parts[parts.length - 1]}`;
	}

	public close(): void {
		this.visible = false;
		this.resetDrawerSessionState();
	}

	/**
	 * Попытка закрыть drawer: если есть несохранённые изменения — показывает диалог подтверждения,
	 * иначе закрывает немедленно. В режиме просмотра изменений нет — закрывает сразу.
	 */
	@HostListener('document:keydown.escape')
	public onEscapeKey(): void {
		if (!this.visible) {
			return;
		}
		if (this.showDiscardDialog) {
			this.keepEditing();
			return;
		}
		if (this.disableRouteDialogVisible) {
			this.closeDisableRouteDialog();
			return;
		}
		this.guardedClose();
	}

	public guardedClose(): void {
		if (this.viewMode || !this.isDirty) {
			this.close();
			return;
		}
		this.showDiscardDialog = true;
	}

	public keepEditing(): void {
		this.showDiscardDialog = false;
	}

	public discardChanges(): void {
		this.showDiscardDialog = false;
		this.formSnapshot = JSON.stringify(this.getFormState());
		this.close();
	}

	public get isDirty(): boolean {
		if (this.viewMode || !this.formSnapshot) {
			return false;
		}
		return JSON.stringify(this.getFormState()) !== this.formSnapshot;
	}

	private getFormState(): object {
		return {
			routeName: this.routeName,
			routeCustomersCanBook: this.routeCustomersCanBook,
			pickupZoneIds: [...this.pickupZoneIds].sort(),
			dropoffZoneIds: [...this.dropoffZoneIds].sort(),
			slots: this.slots.map((s) => ({
				SlotId: s.SlotId,
				PickupZoneId: s.PickupZoneId,
				DropoffZoneId: s.DropoffZoneId,
				DepartureTime: s.DepartureTime,
				FareAmount: s.FareAmount,
				TollAmount: s.TollAmount,
				PremiumFareAmount: s.PremiumFareAmount,
				VehicleCapacity: s.VehicleCapacity,
				BookingWindowMinutes: s.BookingWindowMinutes,
				SlotEnabled: s.SlotEnabled
			}))
		};
	}

	private takeFormSnapshot(): void {
		this.formSnapshot = JSON.stringify(this.getFormState());
	}

	private resetDrawerSessionState(): void {
		this.viewMode = false;
		this.editMode = false;
		this.editRouteId = 0;
		this.routeIdForUpdateSession = 0;
		this.disableRouteDialogVisible = false;
		this.showDiscardDialog = false;
		this.formSnapshot = '';
	}

	public routeStatusPillModifier(): 'active' | 'draft' | 'inactive' | 'unknown' {
		switch (this.routeStatus) {
			case ConnexRouteStatus.Active:
				return 'active';
			case ConnexRouteStatus.Draft:
				return 'draft';
			case ConnexRouteStatus.Inactive:
				return 'inactive';
			default:
				return 'unknown';
		}
	}

	public formatSlotTime12h(hhmm: string): string {
		const parts = (hhmm || '00:00').trim().split(':');
		let h = Number(parts[0]);
		const m = Number(parts[1] ?? 0);
		if (Number.isNaN(h) || Number.isNaN(m)) {
			return hhmm || '—';
		}
		h = ((h % 24) + 24) % 24;
		const d = new Date(2000, 0, 1, h, m, 0, 0);
		return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
	}

	public formatBookingWindowLabel(minutes: number): string {
		if (minutes >= 60 && minutes % 60 === 0) {
			const hrs = minutes / 60;
			return `${hrs} hr${hrs === 1 ? '' : 's'}`;
		}
		if (minutes >= 60) {
			const rounded = Math.round((minutes / 60) * 10) / 10;
			return `${rounded} hrs`;
		}
		return `${minutes} min`;
	}

	public slotDirectionLine(slot: ConnexRouteSlotFormRow): string {
		return `${this.zoneName(slot.PickupZoneId)} → ${this.zoneName(slot.DropoffZoneId)}`;
	}

	public fmtPesoAmount(n: number | null | undefined): string {
		return `₱${ConnexRouteEditDrawerComponent.pesoFmt.format(Number(n ?? 0))}`;
	}

	public inventoryCarsLabel(slot: ConnexRouteSlotFormRow): string {
		const n = Number(slot.VehicleCapacity ?? 0);
		return `${n} car${n === 1 ? '' : 's'}`;
	}

	public addSlot(): void {
		const defPk = this.pickupZoneIds[0] ?? 0;
		const defDo = this.dropoffZoneIds[0] ?? 0;
		this.slots.forEach((s) => {
			s.expanded = false;
		});
		this.slots.push({
			SlotId: 0,
			PickupZoneId: defPk,
			DropoffZoneId: defDo,
			DepartureTime: '05:00',
			FareAmount: 0,
			TollAmount: 0,
			PremiumFareAmount: 0,
			VehicleCapacity: 0,
			BookingWindowMinutes: 240,
			SlotEnabled: true,
			expanded: true,
			assignedDrivers: []
		});
	}

	public duplicateSlot(index: number): void {
		const src = this.slots[index];
		if (!src) {
			return;
		}
		this.slots.forEach((s) => {
			s.expanded = false;
		});
		const copy: ConnexRouteSlotFormRow = {
			SlotId: 0,
			PickupZoneId: src.PickupZoneId,
			DropoffZoneId: src.DropoffZoneId,
			DepartureTime: src.DepartureTime,
			FareAmount: src.FareAmount,
			TollAmount: src.TollAmount,
			PremiumFareAmount: src.PremiumFareAmount,
			VehicleCapacity: src.VehicleCapacity,
			BookingWindowMinutes: src.BookingWindowMinutes,
			SlotEnabled: src.SlotEnabled,
			expanded: true,
			assignedDrivers: [...src.assignedDrivers]
		};
		this.slots.splice(index + 1, 0, copy);
	}

	public removeSlot(index: number): void {
		this.slots.splice(index, 1);
	}

	public toggleSlot(index: number): void {
		const s = this.slots[index];
		if (s) {
			s.expanded = !s.expanded;
		}
	}

	public zoneName(id: number): string {
		if (!id) {
			return '—';
		}
		return this.zoneOptions.find((z) => z.Id === id)?.Name ?? `#${id}`;
	}

	public slotHeaderPrimary(slot: ConnexRouteSlotFormRow): string {
		const t = slot.DepartureTime || '—';
		const pk = this.zoneName(slot.PickupZoneId);
		const dr = this.zoneName(slot.DropoffZoneId);
		return `${t} · ${pk} → ${dr} · ${slot.SlotEnabled ? 'Enabled' : 'Disabled'}`;
	}

	public slotHeaderSecondary(slot: ConnexRouteSlotFormRow): string {
		const fare = slot.FareAmount ?? 0;
		const cap = slot.VehicleCapacity ?? 0;
		const bw = slot.BookingWindowMinutes ?? 0;
		const h =
			bw >= 60 && bw % 60 === 0
				? `${bw / 60}h`
				: bw >= 60
					? `${Math.round((bw / 60) * 10) / 10}h`
					: `${bw} min`;
		return `₱${fare.toLocaleString('en-PH')} fare · ${cap} vehicles · ${h} booking window`;
	}

	public viewRouteIdText(): string {
		return this.editRouteId > 0 ? String(this.editRouteId) : 'N/A';
	}

	public get pickupDropdownOptions(): AdminConnexZoneOption[] {
		return this.zoneOptions.filter((z) => this.pickupZoneIds.includes(z.Id));
	}

	public get dropoffDropdownOptions(): AdminConnexZoneOption[] {
		return this.zoneOptions.filter((z) => this.dropoffZoneIds.includes(z.Id));
	}

	public tollHint(slot: ConnexRouteSlotFormRow): string {
		const toll = Number(slot.TollAmount ?? 0);
		const fare = Number(slot.FareAmount ?? 0);
		if (toll <= 0 || fare <= 0) {
			return 'Optional. Shown separately from fare where applicable.';
		}
		return `₱${toll.toLocaleString('en-PH')} toll is included in the ₱${fare.toLocaleString('en-PH')} total fare.`;
	}

	public async save(saveAsDraft: boolean): Promise<void> {
		if (this.saving) {
			return;
		}
		if (this.editMode && !this.canUpdateRoute) {
			return;
		}
		if (!this.editMode && !this.canCreateRoute) {
			return;
		}
		const body = this.buildSaveRequest(saveAsDraft);
		if (!body) {
			return;
		}
		this.saving = true;
		try {
			const updatingExistingRoute = body.RouteId != null && body.RouteId > 0;
			const res = updatingExistingRoute
				? await this.adminConnex.updateConnexRoute(body, this.messageService)
				: await this.adminConnex.createConnexRoute(body, this.messageService);
			if (!res || res.Success === false) {
				this.messageService.add({
					severity: 'error',
					summary: 'Save failed',
					detail: res?.ErrorMessage ?? 'Unable to save route'
				});
				return;
			}
			this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Route saved successfully.' });
			this.visible = false;
			this.resetDrawerSessionState();
			this.saved.emit();
		} finally {
			this.saving = false;
		}
	}

	private buildSaveRequest(saveAsDraft: boolean): SaveAdminConnexRouteRequest | null {
		if (!saveAsDraft) {
			if (!this.routeName.trim()) {
				this.messageService.add({ severity: 'warn', summary: 'Route name', detail: 'Please enter a route name.' });
				return null;
			}
			if (this.pickupZoneIds.length === 0 || this.dropoffZoneIds.length === 0) {
				this.messageService.add({
					severity: 'warn',
					summary: 'Zones',
					detail: 'Select at least one pickup zone and one drop-off zone.'
				});
				return null;
			}
			if (this.slots.length === 0) {
				this.messageService.add({ severity: 'warn', summary: 'Slots', detail: 'Add at least one departure slot.' });
				return null;
			}
			for (const s of this.slots) {
				if (!this.pickupZoneIds.includes(s.PickupZoneId) || !this.dropoffZoneIds.includes(s.DropoffZoneId)) {
					this.messageService.add({
						severity: 'warn',
						summary: 'Slots',
						detail: 'Each slot must use zones from the route pickup and drop-off lists.'
					});
					return null;
				}
				if (!s.DepartureTime?.trim()) {
					this.messageService.add({ severity: 'warn', summary: 'Slots', detail: 'Each slot needs a departure time.' });
					return null;
				}
				if (s.FareAmount === null || s.FareAmount === undefined) {
					this.messageService.add({ severity: 'warn', summary: 'Slots', detail: 'Each slot needs a fare amount.' });
					return null;
				}
				if (s.VehicleCapacity === null || s.VehicleCapacity === undefined) {
					this.messageService.add({ severity: 'warn', summary: 'Slots', detail: 'Each slot needs a vehicle inventory value.' });
					return null;
				}
				if (s.BookingWindowMinutes === null || s.BookingWindowMinutes === undefined) {
					this.messageService.add({ severity: 'warn', summary: 'Slots', detail: 'Each slot needs a minimum advance booking window.' });
					return null;
				}
			}
		} else {
			if (!this.routeName.trim()) {
				this.messageService.add({
					severity: 'warn',
					summary: 'Route name',
					detail: 'Please enter a route name before saving a draft.'
				});
				return null;
			}
			for (const s of this.slots) {
				if (s.PickupZoneId === 0 || s.DropoffZoneId === 0) {
					continue;
				}
				if (!(s.DepartureTime || '').trim()) {
					this.messageService.add({
						severity: 'warn',
						summary: 'Slots',
						detail: 'Each slot with pickup and drop-off zones needs a departure time.'
					});
					return null;
				}
			}
		}

		return {
			...emptyBaseAdminRequest(),
			RouteId: this.routeIdForUpdateSession > 0 ? this.routeIdForUpdateSession : null,
			Name: this.routeName.trim(),
			SaveAsDraft: saveAsDraft,
			CustomersCanBook: this.routeCustomersCanBook,
			PickupZoneIds: [...this.pickupZoneIds],
			DropoffZoneIds: [...this.dropoffZoneIds],
			Slots: this.slots.map((s) => ({
				SlotId: s.SlotId,
				PickupZoneId: s.PickupZoneId,
				DropoffZoneId: s.DropoffZoneId,
				DepartureTime: this.slotDepartureTimeToApi(s),
				FareAmount: Number(s.FareAmount ?? 0),
				TollAmount: Number(s.TollAmount ?? 0),
				PremiumFareAmount: Number(s.PremiumFareAmount ?? 0),
				VehicleCapacity: Number(s.VehicleCapacity ?? 0),
				BookingWindowMinutes: Number(s.BookingWindowMinutes ?? 0),
				SlotEnabled: s.SlotEnabled
			}))
		};
	}

	private normalizeDepartureForInput(dep: string): string {
		if (!dep) {
			return '05:00';
		}
		const parts = dep.trim().split(':');
		if (parts.length >= 2) {
			const h = Number(parts[0]);
			const m = Number(parts[1]);
			if (!isNaN(h) && !isNaN(m)) {
				return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
			}
		}
		return '05:00';
	}

	private slotDepartureTimeToApi(slot: ConnexRouteSlotFormRow): string {
		const hhmm = this.normalizeDepartureForInput((slot.DepartureTime || '').trim());
		return `${hhmm}:00`;
	}

	private async loadRoutePayload(routeId: number): Promise<boolean> {
		const res = await this.adminConnex.getConnexRouteForEdit(routeId, this.messageService);
		if (!res || res.Success === false) {
			this.messageService.add({
				severity: 'error',
				summary: 'Cannot load route',
				detail: res?.ErrorMessage ?? 'Unknown error'
			});
			return false;
		}
		this.applyFromResponse(res);
		return true;
	}

	private applyFromResponse(res: GetAdminConnexRouteForEditResponse): void {
		const rid = this.readRouteIdFromEditResponse(res);
		if (rid > 0) {
			this.editRouteId = rid;
			this.routeIdForUpdateSession = rid;
		}
		this.routeName = res.Name ?? '';
		const statusStr = this.readRouteStatusFromEditResponse(res);
		this.routeStatus = parseConnexRouteStatusFromApi(statusStr);
		this.routeStatusLabel =
			this.routeStatus !== null
				? connexRouteStatusToDisplayName(this.routeStatus)
				: statusStr.length > 0
					? statusStr
					: '—';
		this.routeCustomersCanBook = !!res.CustomersCanBook;
		this.viewLastUpdatedText = res.LastUpdatedAt
			? `${this.routeStatusLabel} · ${Utils.formatUtcRelativeTimeAgo(res.LastUpdatedAt)}`
			: 'N/A';
		this.viewUpdatedByText = (res.LastUpdatedByName ?? '').trim() || 'N/A';
		this.pickupZoneIds = [...(res.PickupZoneIds ?? [])];
		this.dropoffZoneIds = [...(res.DropoffZoneIds ?? [])];
		this.slots = (res.Slots ?? []).map((s, i) => ({
			SlotId: s.SlotId,
			PickupZoneId: s.PickupZoneId,
			DropoffZoneId: s.DropoffZoneId,
			DepartureTime: this.normalizeDepartureForInput(String((s as { DepartureTime?: unknown }).DepartureTime ?? '')),
			FareAmount: s.FareAmount,
			TollAmount: s.TollAmount,
			PremiumFareAmount: s.PremiumFareAmount,
			VehicleCapacity: s.VehicleCapacity,
			BookingWindowMinutes: s.BookingWindowMinutes,
			SlotEnabled: s.SlotEnabled,
			expanded: i === 0,
			assignedDrivers: (s.AssignedDrivers ?? []).map((d) => ({
				displayName: (d.DisplayName ?? '').trim() || '—'
			}))
		}));
		this.recomputeViewSummary();
	}

	private recomputeViewSummary(): void {
		const n = this.slots.length;
		const vehicles = this.slots.reduce((a, s) => a + Number(s.VehicleCapacity ?? 0), 0);
		this.viewTotalInventory = vehicles;
		this.viewSubtitle = `1 Direction • ${n} Slot${n === 1 ? '' : 's'} • ${vehicles} Vehicle${vehicles === 1 ? '' : 's'}`;
		if (!n) {
			this.viewFareRangeText = '—';
			this.viewBookingWindowText = '—';
			return;
		}
		const totals = this.slots.map(
			(s) => Number(s.FareAmount ?? 0) + Number(s.PremiumFareAmount ?? 0)
		);
		const lo = Math.min(...totals);
		const hi = Math.max(...totals);
		this.viewFareRangeText =
			lo === hi
				? `₱${ConnexRouteEditDrawerComponent.pesoFmt.format(lo)}`
				: `₱${ConnexRouteEditDrawerComponent.pesoFmt.format(lo)}-${ConnexRouteEditDrawerComponent.pesoFmt.format(hi)}`;
		const bws = this.slots.map((s) => Number(s.BookingWindowMinutes ?? 0));
		const bMin = Math.min(...bws);
		const bMax = Math.max(...bws);
		this.viewBookingWindowText =
			bMin === bMax
				? this.formatBookingWindowLabel(bMin)
				: `${this.formatBookingWindowLabel(bMin)}-${this.formatBookingWindowLabel(bMax)}`;
	}

	private resetFormDefaultsForCreate(): void {
		this.routeName = '';
		this.routeStatus = null;
		this.routeStatusLabel = '';
		this.routeCustomersCanBook = true;
		this.pickupZoneIds = [];
		this.dropoffZoneIds = [];
		this.slots = [];
		this.viewSubtitle = '';
		this.viewFareRangeText = '';
		this.viewTotalInventory = 0;
		this.viewBookingWindowText = '';
		this.viewLastUpdatedText = 'N/A';
		this.viewUpdatedByText = 'N/A';
	}

	private readRouteStatusFromEditResponse(res: GetAdminConnexRouteForEditResponse): string {
		const loose = res as GetAdminConnexRouteForEditResponse & { routeStatus?: string };
		return (loose.RouteStatus ?? loose.routeStatus ?? '').trim();
	}

	private readRouteIdFromEditResponse(res: GetAdminConnexRouteForEditResponse): number {
		const loose = res as unknown as { RouteId?: number; routeId?: number };
		const raw = loose.RouteId ?? loose.routeId;
		const n = Number(raw);
		return Number.isFinite(n) && n > 0 ? n : 0;
	}

	private async ensureZonesLoaded(): Promise<void> {
		const res = await this.adminConnex.getConnexZones(this.messageService);
		this.zoneOptions = res?.Items ?? [];
	}
}
